<script setup lang="ts">
import { computed, PropType, ref, Ref, toRefs, watch } from "vue";
import { useApiClient } from "@/api/client";
import ModuleLoading from "@/ui/components/loading/moduleLoading.vue";
import {
	required,
	minLength,
	maxLength,
} from "@/ui/validation/validationRules";
import { useI18n } from "vue-i18n";
import { toastApiResult } from "@/toaster";
import modal from "@/ui/components/modal.vue";
import dayjs from "dayjs";
const { t } = useI18n();

const props = defineProps({
	user: {
		type: Object as PropType<App.Data.RoleDO>,
		require: true,
	},
});

// Emit is not used in this component but required through the tabs-router-view setup.
const emit = defineEmits(["update-user"]);
const { user } = toRefs(props);

const publicApiTokens: Ref<App.Data.PersonalAccessTokenDO[]> = ref([]);
const tableLoading: Ref<boolean> = ref(false);

const headers = computed(() => [
	{
		title: t("users.detail.tokens.headerName"),
		align: "start" as const,
		key: "name",
	},
	{
		title: t("users.detail.tokens.headerStatus"),
		align: "start" as const,
		key: "state",
		sortable: false,
	},
	{
		title: t("users.detail.tokens.headerExpiresAt"),
		align: "start" as const,
		key: "expires_at",
	},
	{
		title: t("users.detail.tokens.headerActions"),
		key: "actions",
		align: "end" as const,
		width: 140,
		sortable: false,
	},
]);

const loadAllTokens = async () => {
	tableLoading.value = true;
	const response = await useApiClient("users").getAllPublicApiTokens(
		user.value.uuid
	);
	if (response.isOk()) {
		publicApiTokens.value = response.value;
	}
	tableLoading.value = false;
};

// The `user` prop is loaded asynchronously by the parent page, so on a direct
// navigation/refresh it can still be undefined when this component mounts.
watch(
	user,
	(newUser) => {
		if (newUser?.uuid) {
			loadAllTokens();
		}
	},
	{ immediate: true }
);

const createPublicApiToken = async () => {
	const response = await useApiClient("users").createPublicApiToken(
		user.value.uuid,
		newTokenName.value
	);
	if (response.isOk()) {
		newTokenPlainText.value = response.value;
		showConfirmation.value = true;
		copyIcon.value = "mdi-content-copy";
		loadAllTokens();
	}
};

const revokePublicApiToken = async (
	publicApiToken: App.Data.PersonalAccessTokenDO
) => {
	const response = await toastApiResult(
		useApiClient("users").revokePublicApiToken(
			user.value.uuid,
			publicApiToken.id
		)
	);
	if (response.isOk()) {
		loadAllTokens();
	}
};

const removePublicApiToken = async (
	publicApiToken: App.Data.PersonalAccessTokenDO
) => {
	const response = await toastApiResult(
		useApiClient("users").removePublicApiToken(
			user.value.uuid,
			publicApiToken.id
		)
	);
	if (response.isOk()) {
		loadAllTokens();
	}
};

const renewPublicApiToken = async (
	publicApiToken: App.Data.PersonalAccessTokenDO
) => {
	const response = await toastApiResult(
		useApiClient("users").renewPublicApiToken(
			user.value.uuid,
			publicApiToken.id
		)
	);
	if (response.isOk()) {
		loadAllTokens();
	}
};

const isNewTokenVisible: Ref<boolean> = ref(false);
const newTokenName: Ref<string> = ref("");
const newTokenPlainText: Ref<string> = ref("");
const showConfirmation: Ref<boolean> = ref(false);
const copyIcon: Ref<string> = ref("mdi-content-copy");

watch(isNewTokenVisible, (visible) => {
	if (visible) {
		newTokenName.value = "";
		newTokenPlainText.value = "";
		showConfirmation.value = false;
	}
});

const copyToken = (token: string) => {
	copyIcon.value = "mdi-check";
	navigator.clipboard.writeText(token);
};
</script>

<template>
	<module-loading>
		<modal
			v-model:visible="isNewTokenVisible"
			v-model:show-confirmation="showConfirmation"
			:title="t('users.detail.newToken.title')"
			icon="fa: fa-light fa-link"
			@submit="createPublicApiToken"
		>
			<v-col class="pa-3" cols="12">
				<div class="pb-2">
					<label class="text-h6">{{ t("users.detail.newToken.name") }}</label>
				</div>
				<v-text-field
					hide-details="auto"
					variant="filled"
					clearable
					v-model="newTokenName"
					:rules="[required(t), maxLength(t, 255), minLength(t, 3)]"
					:hint="t('users.detail.newToken.hint')"
				></v-text-field>
			</v-col>
			<template v-slot:confirmation>
				<v-alert
					color="warning"
					variant="tonal"
					border="start"
					density="compact"
					:icon="false"
					class="mb-5"
				>
					<span class="text-caption">{{
						t("users.detail.newToken.copyToken")
					}}</span>
				</v-alert>
				<div class="w-100">
					<v-text-field
						label="token"
						:model-value="newTokenPlainText"
						:append-inner-icon="copyIcon"
						@click:append-inner="copyToken(newTokenPlainText)"
					></v-text-field>
				</div>
			</template>
		</modal>

		<v-card class="pa-5 mt-5" elevation="1">
			<v-card-title class="d-flex align-center px-0">
				<span class="text-h6">{{ t("users.detail.tokens.title") }}</span>
				<v-spacer />
				<v-btn
					variant="flat"
					color="#85EA2D"
					class="mr-3 text-black"
					target="_blank"
					href="/public-api/documentation#"
				>
					<i class="fa-duotone fa-book mr-3"></i>
					<span>{{ t("users.detail.tokens.swaggerDocumentation") }}</span>
				</v-btn>
				<v-btn variant="flat" color="primary" @click="isNewTokenVisible = true">
					<i class="fa-duotone fa-plus mr-3"></i>
					<span>{{ t("users.detail.tokens.actionCreate") }}</span>
				</v-btn>
			</v-card-title>
			<div class="text-body-2 text-medium-emphasis mb-5">
				{{ t("users.detail.tokens.explanation") }}
			</div>

			<v-data-table
				:headers="headers"
				:items="publicApiTokens"
				:loading="tableLoading"
				:no-data-text="t('users.detail.tokens.noTokens')"
				class="elevation-0"
			>
				<template v-slot:item.name="{ item }">
					<div class="d-flex align-center my-1 font-weight-bold">
						{{ item.name }}
					</div>
				</template>
				<template v-slot:item.state="{ item }">
					<v-chip
						size="small"
						variant="tonal"
						:color="item.state === 'active' ? 'success' : 'warning'"
					>
						{{
							item.state === "active"
								? t("users.detail.tokens.active")
								: t("users.detail.tokens.expired")
						}}
					</v-chip>
				</template>
				<template v-slot:item.expires_at="{ item }">
					{{ dayjs(item.expires_at).format("DD MMM YYYY HH:mm") }}
				</template>
				<template v-slot:item.actions="{ item }">
					<v-btn
						size="x-small"
						icon
						variant="text"
						color="primary"
						@click="renewPublicApiToken(item)"
					>
						<i class="fa-duotone fa-rotate"></i>
						<v-tooltip activator="parent" location="top">
							{{ t("users.detail.tokens.actionExtend") }}
						</v-tooltip>
					</v-btn>
					<v-btn
						size="x-small"
						icon
						variant="text"
						color="warning"
						@click="revokePublicApiToken(item)"
					>
						<i class="fa-light fa-explosion"></i>
						<v-tooltip activator="parent" location="top">
							{{ t("users.detail.tokens.actionRevoke") }}
						</v-tooltip>
					</v-btn>
					<v-btn
						size="x-small"
						icon
						variant="text"
						color="error"
						@click="removePublicApiToken(item)"
					>
						<i class="fa-light fa-trash-alt"></i>
						<v-tooltip activator="parent" location="top">
							{{ t("users.detail.tokens.actionRemove") }}
						</v-tooltip>
					</v-btn>
				</template>
			</v-data-table>
		</v-card>
	</module-loading>
</template>
